/**
 * SupervisorOverridePanel — BACKLOG-1787 (Bill 2026-10-16, "build it").
 *
 * Sits inside the moderate-score (FraudPoint 500–749) follow-up box. The legacy
 * Online Applicants procedure (pp.2-3; smoke-test findings §D2/§D5) lets the
 * application continue without the follow-up ID items only with a SUPERVISOR
 * OVERRIDE. This panel:
 *   - says who may override (roles from `override.supervisor.roles`, never the
 *     officer on the application);
 *   - for a user the SERVER says may grant, shows "Apply supervisor override",
 *     which opens a dialog with a required reason;
 *   - shows the recorded override (who, when, reason) with Revoke for users the
 *     server says may revoke.
 * Every rule is enforced by pcb-retail-api (/v3/carts/{id}/supervisor-overrides);
 * the commit gate reads the same ledger. This panel only reflects it.
 *
 * Renders nothing outside a cart (no cart id) or for a person with no id.
 */

import { useCallback, useEffect, useState } from 'react';
import type { SupervisorOverride, SupervisorOverrideList } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useOptionalCart } from '@/contexts/CartContext';
import { useConfig } from '@/contexts/ConfigContext';
import { Button, ModalShell } from '@/components/primitives';
import { joinRoleNames, supervisorOverrideRolesFrom } from '../shared/supervisorOverrideRoles';

const GATE_KEY = 'FraudPointRisk' as const;
const REASON_MAX = 1000;

export interface SupervisorOverridePanelProps {
  /** peopleJson person.id — the override is recorded for this person. */
  personId: string | undefined;
  /** True while a required follow-up item is still missing. */
  followUpUnmet: boolean;
}

function errorText(e: unknown): string {
  return e instanceof Error && e.message ? e.message : 'Something went wrong. Try again.';
}

function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function SupervisorOverridePanel({ personId, followUpUnmet }: SupervisorOverridePanelProps): JSX.Element | null {
  const api = useApiClient();
  const cartCtx = useOptionalCart();
  const { config } = useConfig();
  const cartId = cartCtx?.cart?.cartId;

  const [list, setList] = useState<SupervisorOverrideList | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'grant' | 'revoke' | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!cartId) return;
    try {
      setList(await api.retail.carts.supervisorOverrides.list(cartId));
      setLoadError(null);
    } catch (e) {
      setLoadError(errorText(e));
    }
  }, [api, cartId]);

  useEffect(() => { void load(); }, [load]);

  if (!cartId || !personId) return null;

  const active: SupervisorOverride | undefined = list?.overrides.find(
    (o) => o.isActive && o.gateKey === GATE_KEY && o.partyId === personId,
  );
  // Nothing to say once the items are collected and no override is on record.
  if (!followUpUnmet && !active) return null;

  const roles = list?.allowedRoles?.length ? list.allowedRoles : supervisorOverrideRolesFrom(config.systemSettings);
  const whoMay = `${joinRoleNames(roles)} (not the officer on this application)`;

  const close = (): void => { setDialog(null); setReason(''); setActionError(null); };

  const submit = async (): Promise<void> => {
    setBusy(true);
    setActionError(null);
    try {
      if (dialog === 'grant') {
        await api.retail.carts.supervisorOverrides.grant(cartId, { gateKey: GATE_KEY, partyId: personId, reason: reason.trim() });
      } else if (dialog === 'revoke' && active) {
        await api.retail.carts.supervisorOverrides.revoke(cartId, active.overrideId, reason.trim() || undefined);
      }
      close();
      await load();
    } catch (e) {
      setActionError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const reasonOk = dialog === 'revoke' || reason.trim().length > 0;

  return (
    <div
      style={{
        marginTop: 'var(--sp-2)',
        padding: '6px 10px',
        background: active ? 'var(--surface-subtle)' : 'transparent',
        border: active ? '1px solid var(--pcb-blue)' : '1px dashed rgba(0,0,0,0.25)',
        borderRadius: 4,
        color: active ? 'var(--pcb-blue-dark)' : 'inherit',
      }}
    >
      {active ? (
        <>
          <div style={{ fontWeight: 600 }}>
            Supervisor override recorded — the follow-up items are not required for this person.
          </div>
          <div style={{ marginTop: 2 }}>
            By {active.grantedByUpn}
            {active.grantedByOfficerCode ? ` (${active.grantedByOfficerCode})` : ''} on {when(active.createdAt)}.
          </div>
          <div style={{ marginTop: 2, whiteSpace: 'pre-wrap' }}>Reason: {active.reason}</div>
          {list?.canRevoke && (
            <div style={{ marginTop: 6 }}>
              <Button variant="ghost" size="sm" onClick={() => setDialog('revoke')}>Revoke override</Button>
            </div>
          )}
        </>
      ) : (
        <>
          <div>Who can override: {whoMay}.</div>
          {list?.canGrant ? (
            <div style={{ marginTop: 6 }}>
              <Button variant="primary" size="sm" onClick={() => setDialog('grant')}>Apply supervisor override</Button>
            </div>
          ) : list?.cannotGrantReason ? (
            <div style={{ marginTop: 2, opacity: 0.8 }}>{list.cannotGrantReason}</div>
          ) : null}
        </>
      )}
      {loadError && (
        <div style={{ marginTop: 4, color: 'var(--pcb-red)' }}>Could not load supervisor overrides: {loadError}</div>
      )}

      {dialog && (
        <ModalShell
          open
          onClose={close}
          title={dialog === 'grant' ? 'Apply supervisor override' : 'Revoke supervisor override'}
          size="sm"
          confirmClose
          isDirty={reason.trim().length > 0}
          footer={
            <>
              <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
              <Button
                variant={dialog === 'grant' ? 'primary' : 'danger'}
                disabled={busy || !reasonOk}
                onClick={() => { void submit(); }}
              >
                {busy ? 'Saving…' : dialog === 'grant' ? 'Apply override' : 'Revoke override'}
              </Button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
            {dialog === 'grant' ? (
              <p style={{ margin: 0 }}>
                This lets the application continue without the follow-up ID items for this person. Your name,
                the time and your reason are recorded on the application.
              </p>
            ) : (
              <p style={{ margin: 0 }}>
                The follow-up items will be required again before the application can be submitted. The original
                override stays on record.
              </p>
            )}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontWeight: 600 }}>
                Reason{dialog === 'grant' ? ' (required)' : ' (optional)'}
              </span>
              <textarea
                value={reason}
                maxLength={REASON_MAX}
                rows={4}
                onChange={(e) => setReason(e.target.value)}
                placeholder={dialog === 'grant'
                  ? 'Why the application can continue without the follow-up items'
                  : 'Why the override is being removed'}
                style={{ padding: '6px 8px', border: '1px solid var(--ink-300, var(--surface-border))', borderRadius: 4, fontSize: 13 }}
              />
              <span style={{ fontSize: 11, opacity: 0.7 }}>{reason.length}/{REASON_MAX}</span>
            </label>
            {actionError && <div style={{ color: 'var(--pcb-red)' }}>{actionError}</div>}
          </div>
        </ModalShell>
      )}
    </div>
  );
}
